/**
 * offlineDB.ts — IndexedDB abstraction for the AMS PWA
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IndexedDB and not localStorage?
 *   • localStorage is synchronous (blocks the main thread, bad for performance)
 *   • localStorage is limited to ~5 MB (not enough for attendance history)
 *   • IndexedDB is async, supports structured data, can store up to hundreds of MB
 *   • IndexedDB works inside Service Workers; localStorage does NOT
 *
 * DATABASE LAYOUT:
 *   DB name   : "ams_db"  (version 1)
 *   Stores:
 *     "cache_store" — API response snapshots
 *         { key: string (URL), data: any, timestamp: number, ttl: number }
 *
 *     "sync_queue" — Pending mutations (clock-in, leave requests, etc.)
 *         { id: number (auto), url: string, method: string, body: string,
 *           headers: object, timestamp: number, retries: number, tag: string }
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const DB_NAME    = "ams_db";
export const DB_VERSION = 1;

export const CACHE_STORE = "cache_store";
export const SYNC_STORE  = "ams-sync-queue";

// Default TTL: 15 minutes (in ms). Stale data is still shown but flagged.
const DEFAULT_TTL = 15 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Open / create the database
// ─────────────────────────────────────────────────────────────────────────────

let _dbPromise: Promise<IDBDatabase> | null = null;

export function getDB(): Promise<IDBDatabase> {
  // Singleton: re-use the same open connection across all calls
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    // Runs when the DB is created for the first time or when version is bumped
    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // ── cache_store: API response snapshots ──────────────────────────────
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        const cs = db.createObjectStore(CACHE_STORE, { keyPath: "key" });
        cs.createIndex("timestamp", "timestamp", { unique: false });
      }

      // ── sync_queue: offline mutation requests ────────────────────────────
      if (!db.objectStoreNames.contains(SYNC_STORE)) {
        const sq = db.createObjectStore(SYNC_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        sq.createIndex("timestamp", "timestamp", { unique: false });
        sq.createIndex("tag",       "tag",       { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => {
      _dbPromise = null; // allow retry on next call
      reject(req.error);
    };
  });

  return _dbPromise;
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic transaction helpers
// ─────────────────────────────────────────────────────────────────────────────

function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return getDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const req = fn(transaction.objectStore(storeName));
        req.onsuccess = () => resolve(req.result);
        req.onerror   = () => reject(req.error);
      })
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CACHE STORE — save and retrieve API response data
// ─────────────────────────────────────────────────────────────────────────────

/** Persists an API response to IndexedDB so it can be served offline. */
export async function setCacheEntry(key: string, data: unknown, ttl = DEFAULT_TTL): Promise<void> {
  await tx<IDBValidKey>(CACHE_STORE, "readwrite", (store) =>
    store.put({ key, data, timestamp: Date.now(), ttl })
  );
}

export interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number;
  isStale: boolean;
}

/** Retrieves a cached entry. Returns null if not found. */
export async function getCacheEntry<T = unknown>(key: string): Promise<CacheEntry<T> | null> {
  const row = await tx<{ key: string; data: T; timestamp: number; ttl: number } | undefined>(
    CACHE_STORE,
    "readonly",
    (store) => store.get(key)
  );
  if (!row) return null;
  return {
    data:      row.data,
    timestamp: row.timestamp,
    ttl:       row.ttl,
    isStale:   Date.now() - row.timestamp > row.ttl,
  };
}

/** Removes a single cache entry. */
export function deleteCacheEntry(key: string): Promise<undefined> {
  return tx<undefined>(CACHE_STORE, "readwrite", (store) => store.delete(key));
}

/** Purges all expired cache entries to free storage. */
export async function purgeExpiredCache(): Promise<number> {
  const db  = await getDB();
  const now = Date.now();
  let purged = 0;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(CACHE_STORE, "readwrite");
    const store = transaction.objectStore(CACHE_STORE);
    const req   = store.openCursor();

    req.onsuccess = () => {
      const cursor = req.result as IDBCursorWithValue | null;
      if (!cursor) { resolve(purged); return; }
      const { timestamp, ttl } = cursor.value;
      if (now - timestamp > ttl) {
        cursor.delete();
        purged++;
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// SYNC QUEUE — queue offline mutations and replay when online
// ─────────────────────────────────────────────────────────────────────────────

export interface SyncItem {
  id?: number;
  url: string;
  method: string;
  body: string | null;
  headers: Record<string, string>;
  timestamp: number;
  retries: number;
  tag: string;       // e.g. "clock-in", "leave-apply" — for grouping
}

/** Add a mutation to the sync queue. */
export async function enqueueSyncItem(item: Omit<SyncItem, "id" | "timestamp" | "retries">): Promise<number> {
  const id = await tx<number>(SYNC_STORE, "readwrite", (store) =>
    store.add({ ...item, timestamp: Date.now(), retries: 0 } as SyncItem) as IDBRequest<number>
  );
  return id;
}

/** Return all queued items (oldest first). */
export async function getAllSyncItems(): Promise<SyncItem[]> {
  return tx<SyncItem[]>(SYNC_STORE, "readonly", (store) =>
    store.index("timestamp").getAll() as IDBRequest<SyncItem[]>
  );
}

/** Return count of items in the queue. */
export async function getSyncQueueCount(): Promise<number> {
  return tx<number>(SYNC_STORE, "readonly", (store) => store.count());
}

/** Delete a successfully synced item. */
export function removeSyncItem(id: number): Promise<undefined> {
  return tx<undefined>(SYNC_STORE, "readwrite", (store) => store.delete(id));
}

/** Increment retry counter on a failed item. Removes if retries > maxRetries. */
export async function handleSyncFailure(id: number, maxRetries = 5): Promise<void> {
  const db   = await getDB();
  const item = await tx<SyncItem | undefined>(SYNC_STORE, "readonly", (s) => s.get(id));
  if (!item) return;

  if (item.retries >= maxRetries) {
    await tx<undefined>(SYNC_STORE, "readwrite", (s) => s.delete(id));
  } else {
    await tx<IDBValidKey>(SYNC_STORE, "readwrite", (s) =>
      s.put({ ...item, retries: item.retries + 1 })
    );
  }
}

/** Clear the entire sync queue (e.g. on logout). */
export async function clearSyncQueue(): Promise<void> {
  const db = await getDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SYNC_STORE, "readwrite");
    const req = tx.objectStore(SYNC_STORE).clear();
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}
